import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { Driver, Stop } from "@api/graphql/generated/generated-types";
import { useAuth } from "@src/common/hooks/useAuth";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";
import { useCourieStore } from "@src/common/lib/store";
import { useDriverComplianceGate } from "@src/driverCompliancePolicies";
import { useDriverScheduleGrouping } from "@src/drivers/hooks/useDriverScheduleGrouping";
import {
  buildTransferJobCreate,
  buildTransferVehicleCreates,
  getSelectedStopDates,
  getTransferOrderIds,
  getTransferShipmentIds,
  pluralizeOrders,
} from "../utils/transferStopsUtils";
import { useTransferRoutingJob } from "./useTransferRoutingJob";
import { useTransferStopDates } from "./useTransferStopDates";

interface UseTransferStopsParams {
  isOpen: boolean;
  selectedStops: Stop[];
  shipmentDate: string;
  onTransferred: (targetDriver: Driver, stopDates: string[]) => void;
  onPending: () => void;
}

/**
 * Business logic for transferring the checked stops to another driver: the
 * target driver pick, the compliance check, resolving every date the moved
 * orders touch, and running the quick-dispatch routing job.
 */
export function useTransferStops({
  isOpen,
  selectedStops,
  shipmentDate,
  onTransferred,
  onPending,
}: UseTransferStopsParams) {
  const { courierId, courieUser, employeeId } = useAuth();
  const { showToast } = useCourieStore();
  const [selectedDriver, setSelectedDriver] =
    useState<Driver | undefined>(undefined);

  const shipmentIds = useMemo(
    () => getTransferShipmentIds(selectedStops),
    [selectedStops]
  );
  const selectedStopDates = useMemo(
    () => getSelectedStopDates(selectedStops, shipmentDate),
    [selectedStops, shipmentDate]
  );
  const driverListDate = useMemo(() => moment(shipmentDate), [shipmentDate]);

  const { driverGroupDefinitions, loading: driverGroupsLoading } =
    useDriverScheduleGrouping({
      courierId: courierId || undefined,
      date: driverListDate,
      enabled: isOpen,
    });

  const {
    check: checkCompliance,
    loading: isCheckingCompliance,
    showWarning,
    warningTypes,
    closeWarning,
    proceedFromWarning,
  } = useDriverComplianceGate();
  const { resolveTransferStopDates, isLoading: isResolvingStopDates } =
    useTransferStopDates();

  const handleApplied = useCallback(
    (stopDates: string[]) => {
      if (!selectedDriver) return;
      showToast({
        type: "success",
        message: `${pluralizeOrders(shipmentIds.length)} transferred`,
      });
      onTransferred(selectedDriver, stopDates);
    },
    [onTransferred, selectedDriver, shipmentIds.length, showToast]
  );

  const { startTransferJob, resetTransferJob, isTransferJobRunning } =
    useTransferRoutingJob({ onApplied: handleApplied, onPending });

  useEffect(() => {
    if (isOpen) {
      setSelectedDriver(undefined);
      resetTransferJob();
    }
  }, [isOpen, resetTransferJob]);

  const executeTransfer = useCallback(() => {
    if (!courierId || !selectedDriver || shipmentIds.length === 0) {
      return;
    }
    const createdBy = employeeId || courieUser?.id;
    if (!createdBy) {
      showToast({
        type: "error",
        message:
          "Could not determine the dispatching user. Please refresh and try again.",
      });
      return;
    }

    // The job needs a vehicle for every date the moved orders touch, including
    // sibling stops on days the route list never showed.
    resolveTransferStopDates(courierId, shipmentIds, selectedStopDates)
      .then((stopDates) =>
        startTransferJob({
          jobCreate: buildTransferJobCreate({
            courierId,
            createdBy,
            shipmentIds,
          }),
          vehicleCreates: buildTransferVehicleCreates(
            selectedDriver,
            stopDates
          ),
          stopDates,
        })
      )
      .catch((error) => {
        showErrorToast(error, showToast);
      });
  }, [
    courieUser,
    courierId,
    employeeId,
    resolveTransferStopDates,
    selectedDriver,
    selectedStopDates,
    shipmentIds,
    showToast,
    startTransferJob,
  ]);

  const handleTransfer = useCallback(() => {
    if (!selectedDriver) return;
    checkCompliance(
      selectedDriver.id,
      getTransferOrderIds(selectedStops),
      executeTransfer
    );
  }, [checkCompliance, executeTransfer, selectedDriver, selectedStops]);

  return {
    selectedDriver,
    setSelectedDriver,
    shipmentIds,
    driverListDate,
    driverGroupDefinitions,
    driverGroupsLoading,
    handleTransfer,
    canTransfer: !!selectedDriver && shipmentIds.length > 0,
    isTransferring:
      isTransferJobRunning || isCheckingCompliance || isResolvingStopDates,
    complianceWarning: {
      isOpen: showWarning,
      warningTypes,
      close: closeWarning,
      proceed: proceedFromWarning,
    },
  };
}
